import { useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  MISSION_PLATFORMS,
  soumettreLivraison,
  type Mission,
  type MissionCandidature,
} from '@/lib/missions';

// ─────────────────────────────────────────────────────────────
// Soumission d'une publication de mission.
//
// Un candidat retenu peut publier sur plusieurs réseaux à la fois.
// Chaque ligne = un réseau (icône au choix) + le lien du post.
// Le bouton « + » ajoute une ligne, la corbeille la retire.
// ─────────────────────────────────────────────────────────────

export interface PublicationLink {
  platform: string;
  url: string;
}

interface Props {
  mission: Mission;
  candidature: MissionCandidature;
  onClose: () => void;
  onSubmitted: () => void;
}

let rowSeq = 0;
const nextRowId = () => {
  rowSeq += 1;
  return rowSeq;
};

interface Row {
  id: number;
  platform: string;
  url: string;
}

/** Relit les publications déjà envoyées (JSON dans `liens.publications`). */
export function readPublications(candidature: MissionCandidature | null): PublicationLink[] {
  const raw = (candidature?.liens || {}) as Record<string, string>;
  const stored = raw.publications;
  if (stored) {
    try {
      const parsed = JSON.parse(stored) as PublicationLink[];
      if (Array.isArray(parsed)) {
        return parsed.filter((p) => p && p.url).map((p) => ({ platform: p.platform || 'instagram', url: p.url }));
      }
    } catch {
      /* on retombe sur le lien unique */
    }
  }
  if (candidature?.lien_publication) {
    return [{ platform: 'instagram', url: candidature.lien_publication }];
  }
  return [];
}

function readMetric(candidature: MissionCandidature, key: string): string {
  const raw = (candidature.liens || {}) as Record<string, string>;
  return String(raw[key] || '').replace(/[^0-9]/g, '');
}

export default function PublicationSubmitModal({ mission, candidature, onClose, onSubmitted }: Props) {
  const existing = useMemo(() => readPublications(candidature), [candidature]);
  const missionPlatforms = (mission.plateformes || []).filter(Boolean);
  const defaultPlatform = missionPlatforms[0] || 'instagram';

  const [rows, setRows] = useState<Row[]>(() => {
    if (existing.length > 0) {
      return existing.map((p) => ({ id: nextRowId(), platform: p.platform, url: p.url }));
    }
    return [{ id: nextRowId(), platform: defaultPlatform, url: '' }];
  });
  const [openRow, setOpenRow] = useState<number | null>(null);
  const [vues, setVues] = useState(() => readMetric(candidature, 'preuve_vues') || String(candidature.vues_declarees || ''));
  const [likes, setLikes] = useState(() => readMetric(candidature, 'preuve_likes'));
  const [commentaires, setCommentaires] = useState(() => readMetric(candidature, 'preuve_commentaires'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const patchRow = (id: number, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const addRow = () => {
    // Propose le réseau demandé par la campagne et pas encore utilisé.
    const used = new Set(rows.map((r) => r.platform));
    const next = missionPlatforms.find((p) => !used.has(p)) || defaultPlatform;
    setRows((prev) => [...prev, { id: nextRowId(), platform: next, url: '' }]);
  };

  const removeRow = (id: number) => {
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));
  };

  const submit = async () => {
    const filled = rows
      .map((r) => ({ platform: r.platform, url: r.url.trim() }))
      .filter((r) => r.url.length > 0);

    if (filled.length === 0) {
      setError('Ajoutez au moins un lien de publication.');
      return;
    }
    const invalid = filled.find((r) => !/^https?:\/\/\S+\.\S+/i.test(r.url) && !/^\S+\.\S{2,}/.test(r.url));
    if (invalid) {
      setError('Un des liens ne ressemble pas à une adresse valide.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const normalized = filled.map((r) => ({
        platform: r.platform,
        url: /^https?:\/\//i.test(r.url) ? r.url : `https://${r.url}`,
      }));

      // 1) Statut + lien principal + vues déclarées + notification au propriétaire.
      await soumettreLivraison(mission, candidature, normalized[0].url, Number(vues) || 0);

      // 2) Tous les liens et les métriques complémentaires dans `liens`.
      const liens = {
        ...(candidature.liens || {}),
        publications: JSON.stringify(normalized),
        preuve_vues: String(Number(vues) || 0),
        preuve_likes: String(Number(likes) || 0),
        preuve_commentaires: String(Number(commentaires) || 0),
      };
      const { error: updError } = await supabase
        .from('mission_candidatures')
        .update({ liens, updated_at: new Date().toISOString() })
        .eq('id', candidature.id);
      if (updError) throw updError;

      onSubmitted();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Envoi impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-start md:items-center justify-center p-3 md:p-6 overflow-y-auto">
      <div className="fixed inset-0 bg-black/45 backdrop-blur-sm" onClick={() => !saving && onClose()}></div>
      <div className="relative w-full max-w-xl my-4 bg-background-50 rounded-xl border border-background-200/70">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-background-200/70">
          <div className="min-w-0">
            <h2 className="text-base font-bold font-heading text-foreground-950">Mes liens de publication</h2>
            <p className="text-xs text-foreground-500 mt-0.5 truncate">{mission.titre}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 cursor-pointer flex-shrink-0"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[68vh] overflow-y-auto">
          <p className="text-[11px] text-foreground-500 bg-background-100 rounded-md px-3 py-2 flex items-start gap-1.5 leading-relaxed">
            <i className="ri-information-line mt-0.5"></i>
            Ajoutez une ligne par réseau utilisé. Chaque publication compte pour la validation des vues.
          </p>

          <div className="space-y-2.5">
            {rows.map((row) => {
              const meta = MISSION_PLATFORMS.find((p) => p.key === row.platform);
              const isOpen = openRow === row.id;
              return (
                <div key={row.id} className="flex items-start gap-2">
                  {/* Sélecteur de réseau (icône) */}
                  <div className="relative flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setOpenRow(isOpen ? null : row.id)}
                      className="w-11 h-11 rounded-lg bg-background-100 border border-background-200/70 text-foreground-700 flex items-center justify-center cursor-pointer hover:border-primary-300 transition-colors"
                      title={meta?.label || row.platform}
                    >
                      <i className={`${meta?.icon || 'ri-share-line'} text-lg`}></i>
                    </button>
                    {isOpen && (
                      <>
                        <button
                          type="button"
                          onClick={() => setOpenRow(null)}
                          className="fixed inset-0 z-[1] cursor-default"
                          aria-label="Fermer la liste"
                        ></button>
                        <div className="absolute z-[2] left-0 mt-1 w-48 rounded-lg border border-background-200/70 bg-background-50 p-1">
                          {MISSION_PLATFORMS.map((p) => (
                            <button
                              key={p.key}
                              type="button"
                              onClick={() => { patchRow(row.id, { platform: p.key }); setOpenRow(null); }}
                              className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-left text-xs font-medium cursor-pointer transition-colors ${
                                p.key === row.platform ? 'bg-primary-50 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
                              }`}
                            >
                              <i className={`${p.icon} text-sm`}></i>
                              {p.label}
                              {p.key === row.platform && <i className="ri-check-line ml-auto text-accent-600"></i>}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Lien de la publication */}
                  <div className="flex-1 min-w-0">
                    <input
                      value={row.url}
                      onChange={(e) => patchRow(row.id, { url: e.target.value })}
                      placeholder={meta ? `Lien de la publication ${meta.label}` : 'https://…'}
                      className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => removeRow(row.id)}
                    disabled={rows.length === 1}
                    className="w-11 h-11 rounded-lg bg-background-100 text-red-500 flex items-center justify-center flex-shrink-0 cursor-pointer hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Retirer cette ligne"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={addRow}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-background-300/70 text-xs font-semibold text-foreground-600 cursor-pointer hover:border-primary-300 hover:text-primary-700 transition-colors"
          >
            <i className="ri-add-line"></i>Ajouter un autre réseau
          </button>

          <div>
            <p className="text-xs font-semibold text-foreground-600 mb-1.5">Statistiques constatées (dernier relevé)</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: 'vues', label: 'Vues', value: vues, set: setVues, icon: 'ri-eye-line' },
                { key: 'likes', label: 'Likes', value: likes, set: setLikes, icon: 'ri-heart-3-line' },
                { key: 'commentaires', label: 'Commentaires', value: commentaires, set: setCommentaires, icon: 'ri-chat-3-line' },
              ].map((f) => (
                <div key={f.key}>
                  <label className="block text-[10px] font-semibold text-foreground-500 mb-1">
                    <i className={`${f.icon} mr-1`}></i>{f.label}
                  </label>
                  <input
                    value={f.value}
                    onChange={(e) => f.set(e.target.value.replace(/[^0-9]/g, ''))}
                    inputMode="numeric"
                    placeholder="0"
                    className="w-full px-2.5 py-2 rounded-lg border border-background-200/70 bg-background-100 text-xs text-foreground-900 focus:outline-none focus:border-primary-300"
                  />
                </div>
              ))}
            </div>
            <p className="mt-2 text-[10px] text-foreground-400 leading-relaxed">
              Le commerce vérifie puis valide les vues retenues. Le montant final est calculé sur le tarif de la campagne.
            </p>
          </div>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-1.5">
              <i className="ri-error-warning-line mt-0.5"></i>
              {error}
            </p>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-background-200/70">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-full bg-background-100 text-foreground-600 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50 transition-colors"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>}
            Envoyer
          </button>
        </div>
      </div>
    </div>
  );
}